import React, { useEffect } from 'lib/react';
import Typography from 'commonComponents/typography';
import {
  EVGrid,
  EVPaper,
  EVTypography,
  makeStyles,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import history from 'store/CreateHistory';
import { ORDER_ROUTE } from 'constants/RouteConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import loadScript from 'utils/loadScript.util';
import { JS_FILES_PATH, NEW_UNIFIED_SKUS } from 'constants/AppConstants';
import { featureFlagSelector } from 'utils/appSelectors.utils';
import { UNIFIED_SKU_SLA_AREA_LAND_USE } from 'constants/FeatureFlag.constants';
import OrderDetails from './components/OrderDetails.component';
import ReportDetails from './components/ReportDetails.component';
import Links from './components/Links.component';
import Buttons from './components/Buttons.component';
import OrderSubmittedMap from './components/orderSubmittedMap/index';
import resetOrderSubmittedReducer from './OrderSubmitted.actions';

const useStyles = makeStyles(() => ({
  orderSubmitted: {
    flex: 1,
    padding: '24px',
    '@media (min-width: 640px)': {
      margin: '16px 24px',
    },
  },
  orderDetailsHeading: {
    '@media (min-width: 640px)': { marginBottom: '24px' },
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '30px',
  },
  orderDetailsContainer: {
    columnGap: '24px',
    flexWrap: 'wrap',
    '@media (min-width: 640px)': {
      flexWrap: 'nowrap',
    },
  },
  buildingsHeading: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '24px 0 16px 0',
  },
}));

function OrderSubmitted() {
  const dispatch = useDispatch();
  const orderSubmitted = useSelector((state) => state.orderSubmitted);
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);

  const isNewSKUOrdered = orderSubmitted?.orderDetails?.some(
    (orderDetail) => NEW_UNIFIED_SKUS.includes(orderDetail.productID)
    && ['regular', 'quick'].includes(orderDetail.delivery.toLowerCase().split(' ')[0].trim()),
  );

  const unifiedSkuSlaAreaLandUseFlag = useSelector(featureFlagSelector(UNIFIED_SKU_SLA_AREA_LAND_USE));

  useEffect(() => {
    if (Object.keys(orderSubmitted).length === 0) {
      history.push(ORDER_ROUTE);
      return () => {
        dispatch(resetOrderSubmittedReducer());
      };
    }

    // Otherwise, load the public script
    const cleanup = loadScript(`${JS_FILES_PATH}/google_enhanced_conversions.js`, 'order-submitted-public-script');
    return () => {
      cleanup?.();
      dispatch(resetOrderSubmittedReducer());
    };
  }, [orderSubmitted, dispatch]);

  if (Object.keys(orderSubmitted).length === 0) return null;

  return (
    <EVPaper square={isMobile} className={classes.orderSubmitted}>
      <EVTypography className={classes.orderDetailsHeading}>{t('order_submitted__order_details')}</EVTypography>
      <EVGrid container wrap="nowrap" className={classes.orderDetailsContainer}>
        <EVGrid id="order-submitted-details" style={{ width: '100%' }} item xs={12} sm={12} md={6} lg={6}>
          <OrderDetails />
        </EVGrid>
        {(orderSubmitted.bluePrintId === '') && <EVGrid style={{ width: '100%' }} item xs={12} sm={12} md={6} lg={6}><OrderSubmittedMap /></EVGrid>}
      </EVGrid>
      <EVTypography className={classes.buildingsHeading}>{t('order_submitted__buildings')}</EVTypography>
      <ReportDetails />

      {unifiedSkuSlaAreaLandUseFlag && isNewSKUOrdered && (
        <Typography variant="subtitle" fontColor="charcoal-600">{t('order_submitted__estimated_delivery_time_message')}</Typography>
      )}
      <Links />
      <Buttons />
    </EVPaper>
  );
}

export default OrderSubmitted;
